import { inject, Injectable } from '@angular/core';
import {
  Actions, createEffect, ofType
} from '@ngrx/effects';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  map,
  of,
  switchMap,
  tap
} from 'rxjs';
import {
  adminLoginSuccess,
  adminLogout,
  adminSessionExpired,
  restoreAdminSession
} from '../../auth/store/auth.actions';
import {
  changePasswordFailure,
  changePasswordSuccess,
  clearUserProfile,
  loadUserProfileFailure,
  loadUserProfileSuccess
} from './user-profile.actions';
import { AuthService } from '../../auth/service/auth.service';
import { IDecodedUserToken } from '../../auth/type/auth.type';
import { UserProfile } from './user-profile.state';
import { UserProfileApiService } from '../api/user-profile.api.service';
import { IChangePassword } from '../type/user-profile.type';

@Injectable()
export class UserProfileEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly userProfileApiService = inject(UserProfileApiService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  loadFromTokenOnAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminLoginSuccess, restoreAdminSession),
      map(({ token }) => this.loadUserProfileFromToken(token))
    )
  );

  /** Clear profile on logout or session expiry */
  clearOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminLogout, adminSessionExpired),
      map(() => clearUserProfile())
    )
  );

  changePassword$ = createEffect(() =>
    switchMap(({ payload }) => this.handleChangePasswordRequest(payload))
  );

  private handleChangePasswordRequest(payload: IChangePassword) {
    return this.userProfileApiService.changePassword(payload).pipe(
      map(() => changePasswordSuccess()),
      catchError((err: HttpErrorResponse) => {
        const error = err.error?.description ?? this.translate.instant('USER_PROFILE.CHANGE_PASSWORD_ERROR', { defaultText: 'Failed to change password.' });
        return of(changePasswordFailure({ error }));
      })
    );
  }

  notifyChangePasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changePasswordSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SUCCESS', { defaultText: 'Success' }),
            detail: this.translate.instant('USER_PROFILE.CHANGE_PASSWORD_SUCCESS', { defaultText: 'Password changed successfully. You will now be logged out.' }),
          });
        })
      ),
    { dispatch: false }
  );

  notifyChangePasswordFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changePasswordFailure),
        tap(({ error }) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR', { defaultText: 'An error occurred.' }),
            detail: error,
          });
        })
      ),
    { dispatch: false }
  );

  logoutAfterPasswordChangeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordSuccess),
      map(() => adminLogout())
    )
  );

  private loadUserProfileFromToken(token: string) {
    const tokenFromStore = token.trim().length > 0
      ? token
      : this.authService.getStoredAuthToken() ?? '';
    const decodedToken = this.authService.decodeAuthToken(tokenFromStore);

    if (decodedToken === null) {
      return loadUserProfileFailure({ error: 'Failed to decode user profile from token' });
    }

    return loadUserProfileSuccess({ profile: this.mapProfileFromToken(decodedToken) });
  }

  private mapProfileFromToken(decodedToken: IDecodedUserToken): UserProfile {
    const parsedId = Number.parseInt(decodedToken.nameid, 10);

    return {
      id: Number.isFinite(parsedId) ? parsedId : 0,
      emailAddress: decodedToken.email,
      firstName: decodedToken.given_name,
      lastName: decodedToken.family_name,
      mobileNumber: '',
      status: 'Active',
      profilePictureFileName: null,
      roleUserId: decodedToken.roleUserId,
      roleId: decodedToken.roleId,
      roleName: decodedToken.roleName,
      role: decodedToken.role,
    };
  }
}
